import React, { lazy, Suspense } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ErrorBoundary from "./utils/ErrorBoundary";
import ProtectedRoute from "./utils/ProtectedRoute";
import Navbar from "./components/Navbar/Navbar";
import { Login } from "./pages";
import "./App.css";
import { useAuth } from "./context/AuthContext";
import permissionConfig from "./components/dashboard/permissionConfig";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import { getAuthRole } from "./utils/authHelpers";


 
const SpecialityMaster = lazy(() => import("./components/dashboard/SpecialityMaster"))
const FrequencyMasters = lazy(() => import("./components/dashboard/FrequencyMasters"))
const ClinicalStabilityMasters = lazy(() => import("./components/dashboard/ClinicalStabilityMasters"))
const NursingProcedures = lazy(() => import("./components/dashboard/NursingProcedures"))
const PhysiotherapyTypes = lazy(() => import("./components/dashboard/PhysiotherapyTypes"))
const ADL_Choices = lazy(() => import("./components/dashboard/ADL_Choices"))
const VitalsCategories = lazy(() => import("./components/dashboard/VitalsCategories"))
const VitalChoices = lazy(() => import("./components/dashboard/VitalChoices"))
const MedicalProfileComorbidity = lazy(() => import("./components/dashboard/MedicalProfileComorbidity"))
const SkillLevelMaster = lazy(() => import("./components/dashboard/SkillLevelMaster"))
const MedicationComplexities = lazy(() => import("./components/dashboard/MedicationComplexities"))
const HomeReadinessMaster = lazy(() => import("./components/dashboard/HomeReadinessMaster"))
const Scenario_Categories = lazy(() => import("./components/dashboard/Scenario_Categories"))
const Scenario_Choices = lazy(() => import("./components/dashboard/Scenario_Choices"))
const ADL_Categories = lazy(() => import("./components/dashboard/ADL_Categories"))
const RiskLevels = lazy(() => import("./components/dashboard/RiskLevels"))
const RiskCategories = lazy(() => import("./components/dashboard/RiskCategories"))
const Medical_equipments = lazy(() => import("./components/dashboard/MedicalEquipments"));
const SystematicStatusMaster = lazy(() => import("./components/dashboard/SystematicStatusMaster"));
const SystematicCategories = lazy(() => import("./components/dashboard/SystematicCategories"))
const Services = lazy(() => import("./components/dashboard/Services"));
const SubServices = lazy(() => import("./components/dashboard/SubServices"));
const CareReasons = lazy(() => import("./components/dashboard/CareReasons"));
const Leads = lazy(() => import("./components/dashboard/Leads"));
const LeadsDetails = lazy(() => import("./components/dashboard/LeadsDetails"));
const Languages = lazy(() => import("./components/dashboard/Languages"));
const Preferedzone = lazy(() => import("./components/dashboard/Preferedzone"));
const PreferedTime = lazy(() => import("./components/dashboard/Preferedtime"));
const Urgencies = lazy(() => import("./components/dashboard/Urgencies"));
const Profile = lazy(() => import("./components/dashboard/Profile"))
const Status = lazy(() => import("./components/dashboard/Status"));
const Googlemapkey = lazy(() => import("./components/dashboard/Googlemapkey"));
const Availableshift = lazy(
  () => import("./components/dashboard/Availableshift"),
);
const DaysAvailable = lazy(
  () => import("./components/dashboard/DaysAvailable"),
);
const EducationLevel = lazy(
  () => import("./components/dashboard/EducationLevel"),
);
const States = lazy(() => import("./components/dashboard/States"));
const Districts = lazy(() => import("./components/dashboard/Districts"));
const Cities = lazy(() => import("./components/dashboard/Cities"));
const Coordinators = lazy(() => import("./components/dashboard/Coordinators"));
const Managers = lazy(() => import("./components/dashboard/Managers"));
const LeadSource = lazy(() => import("./components/dashboard/LeadSource"));
const InteractionType = lazy(
  () => import("./components/dashboard/InteractionType"),
);
const InteractionChannel = lazy(
  () => import("./components/dashboard/InteractionChannel"),
);
const InteractionOutcome = lazy(
  () => import("./components/dashboard/InteractionOutcome"),
);
const FollowUpMode = lazy(() => import("./components/dashboard/FollowUpMode"));
const ResourceType = lazy(() => import("./components/dashboard/ResourceType"));
const AssessmentType = lazy(
  () => import("./components/dashboard/AssessmentType"),
);
const AssessmentMode = lazy(
  () => import("./components/dashboard/AssessmentMode"),
);
const RiskCategory = lazy(() => import("./components/dashboard/RiskCategory"));
const ResourceCategory = lazy(
  () => import("./components/dashboard/ResourceCategory"),
);
const Relations = lazy(() => import("./components/dashboard/Relations"));
const Tariffs = lazy(() => import("./components/dashboard/Tariffs"));
const HospitalFacility = lazy(
  () => import("./components/dashboard/HospitalFacility"),
);
const FacilityService = lazy(
  () => import("./components/dashboard/FacilityService"),
);
const Hospitals = lazy(() => import("./components/dashboard/Hospitals"));
const HospitalDetails = lazy(
  () => import("./components/dashboard/HospitalDetails"),
);
const Carepreneur = lazy(() => import("./components/dashboard/Carepreneur"));
const CarepreneurDetails = lazy(
  () => import("./components/dashboard/CarepreneurDetails"),
);
const Blogposts = lazy(
  () => import("./components/dashboard/Marketing/Blogposts"),
);
const BlogCategory = lazy(
  () => import("./components/dashboard/Marketing/BlogCategory"),
);
const Contacts = lazy(
  () => import("./components/dashboard/Marketing/Contacts"),
);
const SeoUrl = lazy(() => import("./components/dashboard/SeoUrl"));

const PageLoader = () => (
  <div style={{ padding: 40, textAlign: "center" }}>Loading page...</div>
);

const theme = createTheme({
  palette: {
    primary: { main: "#7C3996" },
    background: { default: "#F3F4F6" },
  },
  typography: {
    fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  const { auth } = useAuth();

  const RoleRoute = ({ allowedRoles, children }) => {
    const { auth } = useAuth();
    const currentRole = auth?.role || getAuthRole();

    if (!allowedRoles.includes(currentRole)) {
      return <Navigate to="/" replace />;
    }

    return children;
  };


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GoogleMapsProvider>
        <Router>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={<Navbar />}>
                    <Route
                      index
                      element={
                        (auth?.role || getAuthRole()) === "Admin" ? (
                          <Navigate to="/worklist/myworklist" />
                        ) : null
                      }
                    />

                    <Route path="/settings/services" element={<Services />} />
                    <Route path="/settings/booking-categories" element={<SubServices />} />

                    <Route path="/settings/languages" element={<Languages />} />
                    <Route
                      path="/settings/prefered-zone"
                      element={<Preferedzone />}
                    />
                    <Route path="/settings/status" element={<Status />} />
                    <Route
                      path="/settings/googlemapkey"
                      element={<Googlemapkey />}
                    />
                    <Route
                      path="/settings/availble-shifts"
                      element={<Availableshift />}
                    />
                    <Route
                      path="/settings/days-available"
                      element={<DaysAvailable />}
                    />
                    <Route
                      path="/settings/education-levels"
                      element={<EducationLevel />}
                    />
                    <Route
                      path="/settings/hospital-facilities"
                      element={<HospitalFacility />}
                    />
                    <Route path="/settings/states" element={<States />} />
                    <Route path="/settings/districts" element={<Districts />} />
                    <Route path="/settings/cities" element={<Cities />} />
                    <Route
                      path="/settings/co-ordinator"
                      element={<Coordinators />}
                    />
                    <Route path="/settings/managers" element={<Managers />} />
                    <Route
                      path="/settings/lead-source"
                      element={<LeadSource />}
                    />
                    <Route
                      path="/settings/facility-service"
                      element={<FacilityService />}
                    />
                    <Route
                      path="/settings/interaction-type"
                      element={<InteractionType />}
                    />
                    <Route
                      path="/settings/interaction-channel"
                      element={<InteractionChannel />}
                    />
                    <Route
                      path="/settings/interaction-outcome"
                      element={<InteractionOutcome />}
                    />
                    <Route
                      path="/settings/followupmode"
                      element={<FollowUpMode />}
                    />
                    <Route
                      path="/settings/resourcetype"
                      element={<ResourceType />}
                    />
                    <Route
                      path="/settings/assessment-type"
                      element={<AssessmentType />}
                    />
                    <Route
                      path="/settings/assessment-mode"
                      element={<AssessmentMode />}
                    />
                    <Route
                      path="/settings/risk-category"
                      element={<RiskCategory />}
                    />
                    <Route
                      path="/settings/resource-category"
                      element={<ResourceCategory />}
                    />
                    <Route path="/settings/relations" element={<Relations />} />
                    <Route path="/settings/services" element={<Services />} />
                    <Route path="/settings/medical_equipments" element={<Medical_equipments />} />
                    <Route path="/settings/systematic-categories" element={<SystematicCategories />} />
                    <Route path="/settings/systematic-status-master" element={<SystematicStatusMaster />} />
                    <Route path="/settings/risk-categories" element={<RiskCategories />} />
                    <Route path="/settings/risk-levels" element={<RiskLevels />} />
                    <Route path="/settings/languages" element={<Languages />} />
                    <Route path="/settings/adl-categories" element={<ADL_Categories />} />
                    <Route path="/settings/adl-score" element={<ADL_Choices />} />
                    <Route path="/settings/vital-categories" element={<VitalsCategories />} />
                    <Route path="/settings/vital-choices" element={<VitalChoices />} />
                    <Route path="/settings/comorbidity-master" element={<MedicalProfileComorbidity />} />
                    <Route path="/settings/skill-level-master" element={<SkillLevelMaster />} />
                    <Route path="/settings/medication-complexities" element={<MedicationComplexities />} />
                    <Route path="/settings/home-readiness" element={<HomeReadinessMaster />} />
                    <Route path="/settings/scenario-categories" element={<Scenario_Categories />} />
                    <Route path="/settings/scenario-choices" element={<Scenario_Choices />} />
                    <Route path="/settings/physiotherapy-types" element={<PhysiotherapyTypes />} />
                    <Route path="/settings/nursing-procedures" element={<NursingProcedures />} />
                    <Route path="/settings/clinical-stability-masters" element={<ClinicalStabilityMasters />} />
                    <Route path="/settings/frequency-masters" element={<FrequencyMasters />} />
                    <Route path="/settings/specialities" element={<SpecialityMaster />} />
                    <Route path="/settings/speciality-master" element={<SpecialityMaster />} />
                    <Route path="/settings/care-reasons" element={<CareReasons />} />
                    <Route
                      path="/settings/prefered-zone"
                      element={<Preferedzone />}
                    />
                    <Route
                      path="/settings/preferred-time"
                      element={<PreferedTime />}
                    />
                    <Route
                      path="/settings/urgencies"
                      element={<Urgencies />}
                    />
                    <Route
                      path="/settings/profile"
                      element={<Profile />}
                    />
                    <Route path="/settings/status" element={<Status />} />
                    <Route
                      path="/settings/googlemapkey"
                      element={<Googlemapkey />}
                    />
                    <Route
                      path="/settings/availble-shifts"
                      element={<Availableshift />}
                    />
                    <Route
                      path="/settings/days-available"
                      element={<DaysAvailable />}
                    />
                    <Route
                      path="/settings/education-levels"
                      element={<EducationLevel />}
                    />
                    <Route
                      path="/settings/hospital-facilities"
                      element={<HospitalFacility />}
                    />
                    <Route path="/settings/states" element={<States />} />
                    <Route path="/settings/districts" element={<Districts />} />
                    <Route path="/settings/cities" element={<Cities />} />
                    <Route
                      path="/settings/co-ordinator"
                      element={<Coordinators />}
                    />
                    <Route path="/settings/managers" element={<Managers />} />
                    <Route
                      path="/settings/lead-source"
                      element={<LeadSource />}
                    />
                    <Route
                      path="/settings/facility-service"
                      element={<FacilityService />}
                    />
                    <Route
                      path="/settings/interaction-type"
                      element={<InteractionType />}
                    />
                    <Route
                      path="/settings/interaction-channel"
                      element={<InteractionChannel />}
                    />
                    <Route
                      path="/settings/interaction-outcome"
                      element={<InteractionOutcome />}
                    />
                    <Route
                      path="/settings/followupmode"
                      element={<FollowUpMode />}
                    />
                    <Route
                      path="/settings/resourcetype"
                      element={<ResourceType />}
                    />
                    <Route
                      path="/settings/assessment-type"
                      element={<AssessmentType />}
                    />
                    <Route
                      path="/settings/assessment-mode"
                      element={<AssessmentMode />}
                    />
                    <Route
                      path="/settings/risk-category"
                      element={<RiskCategory />}
                    />
                    <Route
                      path="/settings/resource-category"
                      element={<ResourceCategory />}
                    />
                    <Route path="/settings/relations" element={<Relations />} />
                    <Route path="/settings/tariffs" element={<Tariffs />} />

                    <Route path="/worklist/myworklist" element={<Leads />} />

                    <Route
                      path="/worklist/myworklist/:id"
                      element={<LeadsDetails />}
                    />

                    <Route
                      path="/worklist/carepreneur"
                      element={<Carepreneur />}
                    />
                    <Route
                      path="/worklist/carepreneur/add"
                      element={<CarepreneurDetails />}
                    />
                    <Route
                      path="/worklist/carepreneur/:id"
                      element={<CarepreneurDetails />}
                    />

                    <Route path="/worklist/hospital" element={<Hospitals />} />
                    <Route
                      path="/worklist/hospital/add"
                      element={<HospitalDetails />}
                    />
                    <Route
                      path="/worklist/hospital/:id"
                      element={<HospitalDetails />}
                    />

                    <Route
                      path="/marketing/blog-categories"
                      element={<BlogCategory />}
                    />
                    <Route
                      path="/marketing/blog-posts"
                      element={<Blogposts />}
                    />
                    <Route path="/marketing/contacts" element={<Contacts />} />
                    <Route path="/marketing/seo-url" element={<SeoUrl />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Router>
      </GoogleMapsProvider>
    </ThemeProvider>
  );
}

export default App;
